import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PortfolioContext = createContext(null);

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error("usePortfolio must be used within PortfolioProvider");
  }
  return context;
};

export const PortfolioProvider = ({ children }) => {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [credits, setCredits] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [creditCapacity, setCreditCapacity] = useState(null);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [fxRatesToUsd, setFxRatesToUsd] = useState({ USD: 1, EUR: 1.09, GBP: 1.27, CHF: 1.13, JPY: 0.0067, BTC: 95000, ETH: 3500 });
  const [loading, setLoading] = useState(true);
  const [pricesRefreshing, setPricesRefreshing] = useState(false);
  const [currency, setCurrency] = useState("USD");

  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/assets`, { withCredentials: true });
      setAssets(response.data);
    } catch (error) {
      console.error("Fetch assets error:", error);
    }
  }, []);

  const fetchCredits = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/credits`, { withCredentials: true });
      setCredits(response.data);
    } catch (error) {
      console.error("Fetch credits error:", error);
    }
  }, []);

  const fetchOpportunities = useCallback(async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.minTvl) params.append("min_tvl", filters.minTvl);
      if (filters.tokenFilter) params.append("token_filter", filters.tokenFilter);
      
      const response = await axios.get(`${API_URL}/api/opportunities?${params}`, { withCredentials: true });
      setOpportunities(response.data);
    } catch (error) {
      console.error("Fetch opportunities error:", error);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/portfolio/summary`, { withCredentials: true });
      setSummary(response.data);
    } catch (error) {
      console.error("Fetch summary error:", error);
    }
  }, []);

  const fetchCreditCapacity = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/credits/calculate-capacity`, { withCredentials: true });
      setCreditCapacity(response.data);
    } catch (error) {
      console.error("Fetch credit capacity error:", error);
    }
  }, []);

  const fetchExchangeRates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/exchange-rates`, { withCredentials: true });
      setExchangeRates(response.data);
      // Store the rates_to_usd for currency conversion
      if (response.data.rates_to_usd) {
        setFxRatesToUsd(response.data.rates_to_usd);
      }
    } catch (error) {
      console.error("Fetch exchange rates error:", error);
    }
  }, []);

  // Refresh asset prices from external APIs
  const refreshPrices = useCallback(async () => {
    setPricesRefreshing(true);
    try {
      const response = await axios.post(`${API_URL}/api/assets/refresh-prices`, {}, { withCredentials: true });
      if (response.data.updated > 0) {
        // Refetch assets and summary after price update
        await fetchAssets();
        await fetchSummary();
        await fetchCreditCapacity();
      }
      return response.data;
    } catch (error) {
      console.error("Refresh prices error:", error);
      return { updated: 0 };
    } finally {
      setPricesRefreshing(false);
    }
  }, [fetchAssets, fetchSummary, fetchCreditCapacity]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchAssets(),
      fetchCredits(),
      fetchOpportunities(),
      fetchSummary(),
      fetchCreditCapacity(),
      fetchExchangeRates()
    ]);
    setLoading(false);
    
    // Refresh prices in background after initial load (non-blocking)
    refreshPrices();
  }, [fetchAssets, fetchCredits, fetchOpportunities, fetchSummary, fetchCreditCapacity, fetchExchangeRates, refreshPrices]);

  useEffect(() => {
    if (user) {
      refreshAll();
      setCurrency(user.currency_preference || "USD");
    }
  }, [user, refreshAll]);

  // Asset CRUD
  const createAsset = async (assetData) => {
    const response = await axios.post(`${API_URL}/api/assets`, assetData, { withCredentials: true });
    await fetchAssets();
    await fetchSummary();
    await fetchCreditCapacity();
    return response.data;
  };

  const updateAsset = async (assetId, assetData) => {
    const response = await axios.put(`${API_URL}/api/assets/${assetId}`, assetData, { withCredentials: true });
    await fetchAssets();
    await fetchSummary();
    await fetchCreditCapacity();
    return response.data;
  };

  const deleteAsset = async (assetId) => {
    await axios.delete(`${API_URL}/api/assets/${assetId}`, { withCredentials: true });
    await fetchAssets();
    await fetchSummary();
    await fetchCreditCapacity();
  };

  // Credit CRUD
  const createCredit = async (creditData) => {
    const response = await axios.post(`${API_URL}/api/credits`, creditData, { withCredentials: true });
    await fetchCredits();
    await fetchSummary();
    await fetchCreditCapacity();
    return response.data;
  };

  const updateCredit = async (creditId, creditData) => {
    const response = await axios.put(`${API_URL}/api/credits/${creditId}`, creditData, { withCredentials: true });
    await fetchCredits();
    await fetchSummary();
    await fetchCreditCapacity();
    return response.data;
  };

  const deleteCredit = async (creditId) => {
    await axios.delete(`${API_URL}/api/credits/${creditId}`, { withCredentials: true });
    await fetchCredits();
    await fetchSummary();
    await fetchCreditCapacity();
  };

  // Opportunity CRUD
  const createOpportunity = async (oppData) => {
    const response = await axios.post(`${API_URL}/api/opportunities`, oppData, { withCredentials: true });
    await fetchOpportunities();
    return response.data;
  };

  const deleteOpportunity = async (oppId) => {
    await axios.delete(`${API_URL}/api/opportunities/${oppId}`, { withCredentials: true });
    await fetchOpportunities();
  };

  // AI Risk Assessment
  const getRiskAssessment = async (assetData) => {
    const response = await axios.post(`${API_URL}/api/ai/risk-assessment`, assetData, { withCredentials: true });
    return response.data;
  };

  // Convert value from any currency to the user's display currency
  // fromCurrency: the currency the value is currently in
  // The process: fromCurrency -> USD (using fxRatesToUsd) -> displayCurrency
  const convertValue = useCallback((value, fromCurrency = "USD") => {
    if (!value || isNaN(value)) return 0;
    
    // If currencies match, no conversion needed
    if (fromCurrency === currency) return value;
    
    // Step 1: Convert fromCurrency to USD
    const rateToUsd = fxRatesToUsd[fromCurrency] || 1;
    const valueInUsd = value * rateToUsd;
    
    // Step 2: Convert USD to display currency
    if (currency === "USD") {
      return valueInUsd;
    }
    
    const rateFromUsd = fxRatesToUsd[currency];
    if (!rateFromUsd || rateFromUsd === 0) return valueInUsd;
    
    // To convert FROM USD to another currency, divide by that currency's rate_to_usd
    return valueInUsd / rateFromUsd;
  }, [currency, fxRatesToUsd]);

  // Format a value for display in the user's preferred currency
  // If fromCurrency is provided, the value will be converted first
  const formatCurrency = useCallback((value, showSymbol = true, fromCurrency = "USD") => {
    const converted = convertValue(value, fromCurrency);
    
    if (currency === "BTC") {
      return showSymbol ? `₿${converted.toFixed(6)}` : converted.toFixed(6);
    }
    
    if (currency === "ETH") {
      return showSymbol ? `Ξ${converted.toFixed(4)}` : converted.toFixed(4);
    }
    
    const symbols = { USD: "$", EUR: "€", GBP: "£", CHF: "CHF ", JPY: "¥" };
    const symbol = symbols[currency] || "$";
    
    // Format without decimals for cleaner display
    return showSymbol 
      ? `${symbol}${Math.round(converted).toLocaleString()}`
      : Math.round(converted).toLocaleString();
  }, [currency, convertValue]);
  
  // Convert an asset's value (price * quantity) to user's display currency
  // This helper uses the asset's native currency for proper conversion
  const getAssetValueInDisplayCurrency = useCallback((asset) => {
    const price = asset.current_price || asset.purchase_price || 0;
    const quantity = asset.quantity || 1;
    const assetCurrency = asset.currency || "USD";
    const rawValue = price * quantity;
    return convertValue(rawValue, assetCurrency);
  }, [convertValue]);

  return (
    <PortfolioContext.Provider value={{
      assets,
      credits,
      opportunities,
      summary,
      creditCapacity,
      exchangeRates,
      fxRatesToUsd,
      loading,
      pricesRefreshing,
      currency,
      setCurrency,
      refreshAll,
      refreshPrices,
      fetchOpportunities,
      fetchAssets,
      createAsset,
      updateAsset,
      deleteAsset,
      createCredit,
      updateCredit,
      deleteCredit,
      createOpportunity,
      deleteOpportunity,
      getRiskAssessment,
      convertValue,
      formatCurrency,
      getAssetValueInDisplayCurrency
    }}>
      {children}
    </PortfolioContext.Provider>
  );
};
